/**
 * SlipRecoveryCell — AG Grid cell renderer that shows a small AI sparkle icon
 * per row. On click, opens a popover that lazy-loads the Cognara
 * slip-recovery suggestion for that single task. Cached server-side per
 * task fingerprint so repeated opens are instant.
 *
 * Props injected via AG Grid cellRendererParams:
 *   projectId   — capital project id (e.g. proj_001)
 *   scheduleId  — schedule_projects.id (the macro schedule the task belongs to)
 */
import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sparkles, Loader2, Crown, Target, Clock, RefreshCw,
         GitBranch, GitCommitHorizontal, TrendingDown, Search,
         Layers, HardHat } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const DISCIPLINE_ROWS = [
  ['ENGINEERING', 'ENG', 'engineering'],
  ['PROCUREMENT', 'PROC', 'procurement'],
  ['FABRICATION_CONSTRUCTION', 'F&C', 'fabrication_construction'],
  ['PRECOMM_COMMISSIONING', 'PC&C', 'precommissioning'],
];

const fmtNum = (v) => (v == null ? '—' : v);

const SEVERITY = {
  CRITICAL: { fg: '#dc2626', bg: 'rgba(220,38,38,0.12)', label: 'CRITICAL' },
  HIGH:     { fg: '#ea580c', bg: 'rgba(234,88,12,0.12)', label: 'HIGH' },
  MEDIUM:   { fg: '#ca8a04', bg: 'rgba(202,138,4,0.12)', label: 'MEDIUM' },
  LOW:      { fg: '#10b981', bg: 'rgba(16,185,129,0.12)', label: 'LOW' },
};

function SlipRecoveryCell(props) {
  const { projectId, scheduleId, data } = props;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  // Lightweight client-side hint of slip — drives the icon colour pre-fetch.
  const localHint = computeLocalHint(data);
  const tint = SEVERITY[localHint] || SEVERITY.LOW;

  const taskId = data?.id;
  const disabled = !taskId || !projectId || !scheduleId || !!data?.is_summary;

  const load = async (force = false) => {
    if (disabled) return;
    setLoading(true); setError(null);
    try {
      const r = await api.get(
        `/v1/projects/${projectId}/ai-analyst/schedules/${scheduleId}/tasks/${taskId}/slip-recovery${force ? '?force=true' : ''}`,
        { timeout: 90000 }
      );
      setPayload(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to fetch');
    } finally { setLoading(false); }
  };

  const onOpenChange = (next) => {
    setOpen(next);
    if (next && !payload && !loading) load(false);
  };

  if (disabled) {
    return <span className="text-slate-600 text-xs">—</span>;
  }

  const recovery = payload?.recovery;
  const sev = SEVERITY[recovery?.severity] || tint;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid={`slip-recovery-btn-${taskId}`}
          title="AI slip-recovery suggestion"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center rounded transition-transform hover:scale-110"
          style={{
            width: 22, height: 22,
            background: tint.bg,
            border: `1px solid ${tint.fg}55`,
            color: tint.fg,
          }}
        >
          <Sparkles className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="left"
        className="w-[560px] p-0 border-0 overflow-y-auto max-h-[var(--radix-popover-content-available-height)]"
        collisionPadding={12}
        data-testid={`slip-recovery-popover-${taskId}`}
        style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
      >
        <div className="flex items-center gap-2 px-3 py-2"
             style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          <Sparkles className="w-3.5 h-3.5" style={{ color: '#06b6d4' }} />
          <span className="text-[11px] font-bold tracking-wide uppercase"
                style={{ color: 'var(--text-primary)' }}>AI Slip-Recovery</span>
          {recovery && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold ml-auto"
                  style={{ background: sev.bg, color: sev.fg }}>
              {sev.label}
            </span>
          )}
        </div>

        <div className="p-3 space-y-2">
          <div className="text-[10px] font-mono opacity-60" style={{ color: 'var(--text-muted)' }}>
            {data?.wbs_code} · {(data?.name || '').slice(0, 60)}
          </div>

          {loading && (
            <div className="flex items-center gap-2 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>Asking Cognara…</span>
            </div>
          )}

          {error && (
            <div className="text-xs py-2" style={{ color: '#dc2626' }}>{error}</div>
          )}

          {recovery && !loading && (
            <>
              <div className="rounded p-2"
                   style={{ background: 'var(--surface-bg)', border: `1px solid ${sev.fg}33` }}>
                <div className="text-[11px] uppercase tracking-wider mb-1 flex items-center gap-1"
                     style={{ color: sev.fg }}>
                  <Target className="w-3 h-3" /> Recommended action
                </div>
                <div className="text-[12px] font-semibold leading-snug"
                     style={{ color: 'var(--text-primary)' }}>
                  {recovery.suggestion}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded p-2"
                     style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}>
                  <div className="text-[9px] uppercase tracking-wider opacity-60" style={{ color: 'var(--text-muted)' }}>Owner</div>
                  <div className="text-[11px] font-semibold flex items-center gap-1"
                       style={{ color: 'var(--text-primary)' }}>
                    <Crown className="w-3 h-3" style={{ color: '#f59e0b' }} />
                    {recovery.owner || '—'}
                  </div>
                </div>
                <div className="rounded p-2"
                     style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}>
                  <div className="text-[9px] uppercase tracking-wider opacity-60" style={{ color: 'var(--text-muted)' }}>By when</div>
                  <div className="text-[11px] font-semibold flex items-center gap-1"
                       style={{ color: 'var(--text-primary)' }}>
                    <Clock className="w-3 h-3" style={{ color: '#06b6d4' }} />
                    {recovery.by_when || '—'}
                  </div>
                </div>
              </div>

              {recovery.rationale && (
                <div className="text-[10.5px] leading-snug italic"
                     style={{ color: 'var(--text-secondary)' }}>
                  Why: {recovery.rationale}
                </div>
              )}

              {/* Root cause */}
              {recovery.root_cause && (
                <div className="rounded p-2"
                     style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}
                     data-testid={`slip-root-cause-${taskId}`}>
                  <div className="text-[10px] uppercase tracking-wider mb-0.5 flex items-center gap-1"
                       style={{ color: '#0ea5e9' }}>
                    <Search className="w-3 h-3" /> Root Cause
                  </div>
                  <div className="text-[11px] leading-snug" style={{ color: 'var(--text-primary)' }}>
                    {recovery.root_cause}
                  </div>
                </div>
              )}

              {/* Predecessor impact */}
              {(recovery.predecessor_impact || (payload?.context?.predecessors?.length ?? 0) > 0) && (
                <div className="rounded p-2"
                     style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}
                     data-testid={`slip-pred-impact-${taskId}`}>
                  <div className="text-[10px] uppercase tracking-wider mb-0.5 flex items-center gap-1"
                       style={{ color: '#a855f7' }}>
                    <GitCommitHorizontal className="w-3 h-3" /> Impact of Predecessors
                    <span className="ml-auto text-[9px] opacity-60">
                      {payload?.context?.predecessors?.length ?? 0} upstream
                    </span>
                  </div>
                  <div className="text-[11px] leading-snug" style={{ color: 'var(--text-primary)' }}>
                    {recovery.predecessor_impact}
                  </div>
                  {payload?.context?.predecessors?.slice(0, 4).map((p, i) => (
                    <div key={i} className="text-[9.5px] font-mono mt-0.5 flex justify-between gap-2"
                         style={{ color: 'var(--text-muted)' }}>
                      <span className="truncate flex-1">{p.wbs_code} · {p.name}</span>
                      <span>
                        {p.pct_complete != null ? `${Math.round(p.pct_complete)}%` : '—'}
                        {p.slip_days != null && p.slip_days > 0 && (
                          <b style={{ color: '#dc2626', marginLeft: 4 }}>+{p.slip_days}d</b>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Successor impact */}
              {(recovery.successor_impact || (payload?.context?.successors?.length ?? 0) > 0) && (
                <div className="rounded p-2"
                     style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}
                     data-testid={`slip-succ-impact-${taskId}`}>
                  <div className="text-[10px] uppercase tracking-wider mb-0.5 flex items-center gap-1"
                       style={{ color: '#f97316' }}>
                    <GitBranch className="w-3 h-3" /> Impact on Successors
                    <span className="ml-auto text-[9px] opacity-60">
                      {payload?.context?.successors?.length ?? 0} downstream
                    </span>
                  </div>
                  <div className="text-[11px] leading-snug" style={{ color: 'var(--text-primary)' }}>
                    {recovery.successor_impact}
                  </div>
                  {payload?.context?.successors?.slice(0, 4).map((s, i) => (
                    <div key={i} className="text-[9.5px] font-mono mt-0.5 flex justify-between gap-2"
                         style={{ color: 'var(--text-muted)' }}>
                      <span className="truncate flex-1">
                        {s.is_critical && <span style={{ color: '#dc2626', marginRight: 4 }}>★</span>}
                        {s.wbs_code} · {s.name}
                      </span>
                      <span>
                        {s.total_slack != null && (
                          <b style={{ color: (s.total_slack < 0) ? '#dc2626' : 'var(--text-muted)' }}>
                            slack {s.total_slack}d
                          </b>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* The four fronts that deliver this bar */}
              {payload?.picture && (
                <div className="rounded p-2"
                     style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}
                     data-testid={`slip-picture-${taskId}`}>
                  <div className="text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1"
                       style={{ color: '#14b8a6' }}>
                    <Layers className="w-3 h-3" /> Whole picture · E / P / F&amp;C / PC
                    <span className="ml-auto text-[9px] opacity-60">
                      {payload.picture.totalLinked ?? 0} linked
                    </span>
                  </div>

                  {!payload.picture.linked && (
                    <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      {payload.picture.note}
                    </div>
                  )}

                  {DISCIPLINE_ROWS.map(([key, short, promptKey]) => {
                    const d = payload.picture.disciplines?.[key];
                    const line = recovery.discipline_picture?.[promptKey];
                    if (!d && !line) return null;
                    return (
                      <div key={key} className="mt-1" data-testid={`slip-picture-${promptKey}-${taskId}`}>
                        <div className="flex items-center gap-1.5 text-[10px] font-mono">
                          <span className="px-1 rounded font-bold"
                                style={{ background: 'rgba(20,184,166,0.15)', color: '#14b8a6' }}>
                            {short}
                          </span>
                          {d && (
                            <span style={{ color: 'var(--text-muted)' }}>
                              {d.count} items · {d.progressPct}%
                              {d.slipping > 0 && (
                                <b style={{ color: '#dc2626', marginLeft: 4 }}>{d.slipping} slipping</b>
                              )}
                              {d.worstVarianceDays ? ` · worst +${d.worstVarianceDays}d` : ''}
                            </span>
                          )}
                        </div>
                        {line && (
                          <div className="text-[11px] leading-snug" style={{ color: 'var(--text-primary)' }}>
                            {line}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {payload.picture.resources && (
                    <div className="mt-1.5 pt-1.5" style={{ borderTop: '1px solid var(--border-subtle)' }}
                         data-testid={`slip-picture-resources-${taskId}`}>
                      <div className="text-[10px] uppercase tracking-wider flex items-center gap-1"
                           style={{ color: '#f59e0b' }}>
                        <HardHat className="w-3 h-3" /> Manpower &amp; machinery
                      </div>
                      {recovery.discipline_picture?.resources && (
                        <div className="text-[11px] leading-snug" style={{ color: 'var(--text-primary)' }}>
                          {recovery.discipline_picture.resources}
                        </div>
                      )}
                      <div className="text-[9.5px] font-mono mt-0.5 flex flex-wrap gap-x-3"
                           style={{ color: 'var(--text-muted)' }}>
                        <span>crew {fmtNum(payload.picture.resources.manpower?.avgActualHeadcount)}
                          {payload.picture.resources.manpower?.avgPlannedHeadcount != null &&
                            ` / ${fmtNum(payload.picture.resources.manpower.avgPlannedHeadcount)} planned`}
                        </span>
                        {payload.picture.resources.manpower?.avgProductivityFactor != null && (
                          <span>productivity {payload.picture.resources.manpower.avgProductivityFactor}</span>
                        )}
                        <span>plant util {fmtNum(payload.picture.resources.machinery?.avgUtilisationPct)}%</span>
                        <span>op hrs {fmtNum(payload.picture.resources.machinery?.operatingHours)}</span>
                        {payload.picture.resources.productivity?.physicalPct != null && (
                          <span>fronts {payload.picture.resources.productivity.physicalPct}% done</span>
                        )}
                      </div>
                    </div>
                  )}

                  {recovery.discipline_picture?.verdict && (
                    <div className="text-[11px] leading-snug mt-1.5 font-semibold"
                         style={{ color: '#14b8a6' }}
                         data-testid={`slip-picture-verdict-${taskId}`}>
                      {recovery.discipline_picture.verdict}
                    </div>
                  )}
                </div>
              )}

              {/* Catch-up plan */}
              {Array.isArray(recovery.catchup_plan) && recovery.catchup_plan.length > 0 && (
                <div className="rounded p-2"
                     style={{ background: 'var(--surface-bg)', border: `1px solid ${sev.fg}55` }}
                     data-testid={`slip-catchup-${taskId}`}>
                  <div className="text-[10px] uppercase tracking-wider mb-0.5 flex items-center gap-1"
                       style={{ color: sev.fg }}>
                    <TrendingDown className="w-3 h-3" /> Catch-Up Plan
                  </div>
                  <ol className="text-[11px] leading-snug list-decimal pl-4 space-y-0.5"
                      style={{ color: 'var(--text-primary)' }}>
                    {recovery.catchup_plan.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}

              {payload?.metrics && payload.isSlipping && (
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-mono"
                     style={{ color: 'var(--text-muted)' }}>
                  {payload.metrics.slip_days != null && (
                    <span>slip: <b style={{ color: '#dc2626' }}>{payload.metrics.slip_days}d</b></span>
                  )}
                  <span>prog: <b>{payload.metrics.pct_complete}%</b></span>
                  {payload.metrics.is_critical && <span style={{ color: '#dc2626' }}>★ critical path</span>}
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <div className="text-[9px] opacity-50 flex-1" style={{ color: 'var(--text-muted)' }}>
                  Confidence: {Math.round((recovery.confidence || 0) * 100)}% · Powered by Cognara
                </div>
                <button onClick={(e) => { e.stopPropagation(); load(true); }}
                        title="Re-ask AI (force refresh)"
                        className="p-1 rounded hover:opacity-80"
                        style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}
                        data-testid={`slip-recovery-refresh-${taskId}`}>
                  <RefreshCw className="w-3 h-3" />
                </button>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Quick client-side severity tint so the icon colour signals slip even before
// the user opens the popover.
function computeLocalHint(t) {
  if (!t || t.is_summary) return 'LOW';
  if (t.is_critical) return 'CRITICAL';
  if ((t.total_slack ?? 0) < 0) return 'HIGH';
  const pct = t.percent_complete ?? 0;
  const blFinish = t.baseline_finish ? new Date(t.baseline_finish) : null;
  const schedFinish = t.scheduled_finish ? new Date(t.scheduled_finish) : null;
  if (blFinish && schedFinish && schedFinish > blFinish) {
    const days = Math.round((schedFinish - blFinish) / 86400000);
    if (days > 30) return 'HIGH';
    if (days > 0)  return 'MEDIUM';
  }
  if (blFinish && blFinish < new Date() && pct < 100) return 'MEDIUM';
  return 'LOW';
}

export default SlipRecoveryCell;
